import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Route, Router } from '@angular/router';
import { filter } from 'rxjs';

/** A single rendered breadcrumb entry. A missing `link` renders as plain, non-clickable text. */
export interface Crumb {
  label: string;
  link?: string;
}

/** Metadata attached to a route via `data.breadcrumb`. */
export interface BreadcrumbData {
  /** Human-readable label shown in the trail. */
  label: string;
  /**
   * The parent this page sits under. Either a route path from the flat route
   * config (e.g. `sku-list`) or the key of a virtual section (e.g. `mein-konto`).
   * Present only on pages that live at least one level below a section root;
   * pages without a `parent` never render a trail on their own.
   */
  parent?: string;
}

/**
 * Virtual breadcrumb roots — logical groupings that are not routes of their own
 * and render as plain, non-clickable text at the top of a trail. Keyed by the
 * identifier used in `data.breadcrumb.parent` (e.g. the "Mein Konto" area).
 */
const VIRTUAL_SECTIONS: Record<string, string> = {
  'mein-konto': $localize`:@@breadcrumb.section.myAccount:Mein Konto`,
};

/**
 * Builds the breadcrumb trail for the current route and tracks which virtual
 * section that route belongs to.
 *
 * The app's routing is flat (no nested `children`), so the hierarchy cannot be
 * derived from the `ActivatedRoute` tree. Instead each deep route declares its
 * parent explicitly via `data.breadcrumb.parent`, and this service walks that
 * chain — through real routes and virtual section roots — to assemble the trail.
 *
 * A trail is produced only when the active route has a `parent`, i.e. you are at
 * least two levels deep. Flat top-level pages return an empty trail and show no
 * breadcrumb.
 *
 * `section` exposes the virtual root reached while walking (e.g. `mein-konto`),
 * or `null` — used to show the contextual account sidebar only on account pages.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);

  readonly crumbs = signal<Crumb[]>([]);
  readonly section = signal<string | null>(null);

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        const { crumbs, section } = this.build();
        this.crumbs.set(crumbs);
        this.section.set(section);
      });
  }

  private build(): { crumbs: Crumb[]; section: string | null } {
    let leaf: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (leaf?.firstChild) {
      leaf = leaf.firstChild;
    }

    const data = leaf?.data?.['breadcrumb'] as BreadcrumbData | undefined;
    if (!data?.parent) {
      return { crumbs: [], section: null };
    }

    // Walk the parent chain (parents may themselves have parents, ending at a
    // virtual section root) and prepend each ancestor, so the trail reads
    // root-first.
    const ancestors: Crumb[] = [];
    let section: string | null = null;
    let parentPath: string | undefined = data.parent;
    while (parentPath) {
      const virtualLabel = VIRTUAL_SECTIONS[parentPath];
      if (virtualLabel) {
        section = parentPath;
        ancestors.unshift({ label: virtualLabel });
        break;
      }

      const config = this.findConfig(parentPath);
      const parentData = config?.data?.['breadcrumb'] as BreadcrumbData | undefined;
      if (!parentData) {
        break;
      }
      ancestors.unshift({ label: parentData.label, link: '/' + parentPath });
      parentPath = parentData.parent;
    }

    return { crumbs: [...ancestors, { label: data.label, link: this.router.url }], section };
  }

  private findConfig(path: string): Route | undefined {
    return this.router.config.find((route) => route.path === path);
  }
}
